import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import { useToast } from '../contexts/ToastContext';
import { usePortal } from '../contexts/PortalContext';
import { copyText } from '../utils/portalMode';
import { BrandFields } from '../components/BrandFields';
import { brandFromTenant, brandToPayload, emptyTenantForm } from '../utils/tenantForm';

function PlatformSettings() {
  const toast = useToast();
  const { partnerPortalOrigin, refreshSettings } = usePortal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partnerPortalHost, setPartnerPortalHost] = useState('');
  const [appDomain, setAppDomain] = useState('');
  const [brandForm, setBrandForm] = useState(emptyTenantForm);
  const [sharedEnabled, setSharedEnabled] = useState(false);
  const [sharedPhoneNumberId, setSharedPhoneNumberId] = useState('');
  const [sharedWabaId, setSharedWabaId] = useState('');
  const [sharedPhoneNumber, setSharedPhoneNumber] = useState('');
  const [sharedDisplayName, setSharedDisplayName] = useState('Inaiy');
  const [sharedAccessToken, setSharedAccessToken] = useState('');
  const [hasAccessToken, setHasAccessToken] = useState(false);
  const [sharedReady, setSharedReady] = useState(false);
  const [sharedStats, setSharedStats] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const response = await api.post('/admin/platform-settings/get', {});
        const settings = response.data?.data?.settings || {};
        setPartnerPortalHost(settings.partnerPortalHost || '');
        setAppDomain(settings.appDomain || '');
        setBrandForm({
          ...emptyTenantForm,
          ...brandFromTenant({ brand: settings.defaultBrand || {} }),
        });
        const sw = settings.sharedWhatsApp || {};
        setSharedEnabled(Boolean(sw.enabled));
        setSharedPhoneNumberId(sw.phoneNumberId || '');
        setSharedWabaId(sw.wabaId || '');
        setSharedPhoneNumber(sw.phoneNumber || '');
        setSharedDisplayName(sw.displayName || 'Inaiy');
        setHasAccessToken(Boolean(sw.hasAccessToken));
        setSharedReady(Boolean(sw.ready));
        setSharedStats(sw.stats || null);
        setSharedAccessToken('');
      } catch (error) {
        toast.error('Failed to load platform settings');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [toast]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const sharedWhatsApp = {
        enabled: sharedEnabled,
        phoneNumberId: sharedPhoneNumberId,
        wabaId: sharedWabaId,
        phoneNumber: sharedPhoneNumber,
        displayName: sharedDisplayName,
      };
      if (sharedAccessToken.trim()) {
        sharedWhatsApp.accessToken = sharedAccessToken.trim();
      }
      const response = await api.post('/admin/platform-settings/update', {
        partnerPortalHost,
        appDomain: appDomain || undefined,
        defaultBrand: brandToPayload(brandForm),
        sharedWhatsApp,
      });
      const sw = response.data?.data?.settings?.sharedWhatsApp || {};
      setHasAccessToken(Boolean(sw.hasAccessToken));
      setSharedReady(Boolean(sw.ready));
      setSharedStats(sw.stats || null);
      setSharedAccessToken('');
      await refreshSettings();
      toast.success('Platform settings updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading settings..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Platform settings"
        description="Reseller portal, default brand, and the Inaiy shared WhatsApp number used by the Free plan."
      />
      <div className="card">
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">App domain</label>
            <input
              className="form-input"
              value={appDomain}
              onChange={(e) => setAppDomain(e.target.value)}
              placeholder="inaiy.com"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Partner portal host</label>
            <input
              className="form-input"
              value={partnerPortalHost}
              onChange={(e) => setPartnerPortalHost(e.target.value)}
              placeholder="partner.inaiy.com"
              required
            />
            <span className="form-hint">Hostname only. Example: partner.inaiy.com</span>
          </div>
          <div className="portal-url-card">
            <div>
              <div className="stat-card__label">Login URL partners receive</div>
              <div className="portal-url">{partnerPortalOrigin || `https://${partnerPortalHost}`}</div>
            </div>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={async () => {
                const url = partnerPortalOrigin || `https://${partnerPortalHost}`;
                const ok = await copyText(url);
                toast[ok ? 'success' : 'error'](ok ? 'Copied' : 'Copy failed');
              }}
            >
              Copy URL
            </button>
          </div>
          <BrandFields
            formData={brandForm}
            setField={(key, value) => setBrandForm((prev) => ({ ...prev, [key]: value }))}
            hint="Default look for inaiy.com and any host without a partner/customer theme. Empty color keeps #1DA1FF."
          />

          <hr style={{ margin: '24px 0', border: 0, borderTop: '1px solid var(--border, #e5e7eb)' }} />
          <h3 style={{ marginBottom: 8, fontSize: 16 }}>Inaiy trial WhatsApp number (Free plan)</h3>
          <p className="form-hint" style={{ marginBottom: 16 }}>
            Same idea as Wati: Free workspaces use this Inaiy-owned Meta number. Customers scan a QR / wa.me
            to message the workspace. Paid plans connect their own number instead.
            Uncheck Enable to kill-switch shared messaging. Leave the access token blank to keep the existing token.
          </p>
          {hasAccessToken || sharedPhoneNumberId ? (
            <div className="portal-url-card" style={{ marginBottom: 16 }}>
              <div>
                <div className="stat-card__label">Status</div>
                <div className="portal-url" style={{ fontSize: 14 }}>
                  {sharedEnabled ? 'Enabled' : 'Disabled (kill switch)'}
                  {sharedReady
                    ? ' · Ready for Free tenants (QR + messaging)'
                    : ' · Not ready — need phone, Meta phone ID, and token'}
                </div>
                {sharedPhoneNumber ? (
                  <div className="form-hint" style={{ marginTop: 8 }}>
                    Customer-facing number: {sharedPhoneNumber}
                  </div>
                ) : null}
                {sharedStats ? (
                  <div className="form-hint" style={{ marginTop: 8 }}>
                    Active customer bindings: {sharedStats.activeBindings ?? 0}
                    {' · '}
                    New in last 7 days: {sharedStats.bindingsLast7Days ?? 0}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={sharedEnabled}
                onChange={(e) => setSharedEnabled(e.target.checked)}
              />
              Enable Inaiy shared number for Free / trial workspaces
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Display name (shown in app)</label>
            <input
              className="form-input"
              value={sharedDisplayName}
              onChange={(e) => setSharedDisplayName(e.target.value)}
              placeholder="Inaiy"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Customer-facing phone (E.164) *</label>
            <input
              className="form-input"
              value={sharedPhoneNumber}
              onChange={(e) => setSharedPhoneNumber(e.target.value)}
              placeholder="+9198XXXXXXXX"
              required={sharedEnabled}
            />
            <span className="form-hint">
              Required for QR / wa.me on Free dashboards. Use the same number registered in Meta.
            </span>
          </div>
          <div className="form-group">
            <label className="form-label">Meta phone number ID *</label>
            <input
              className="form-input"
              value={sharedPhoneNumberId}
              onChange={(e) => setSharedPhoneNumberId(e.target.value)}
              placeholder="phone_number_id from Meta"
              required={sharedEnabled}
            />
          </div>
          <div className="form-group">
            <label className="form-label">WABA ID</label>
            <input
              className="form-input"
              value={sharedWabaId}
              onChange={(e) => setSharedWabaId(e.target.value)}
              placeholder="WhatsApp Business Account id"
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              Permanent access token
              {hasAccessToken ? ' (saved — paste a new value to rotate)' : ' *'}
            </label>
            <input
              className="form-input"
              type="password"
              autoComplete="off"
              value={sharedAccessToken}
              onChange={(e) => setSharedAccessToken(e.target.value)}
              placeholder={hasAccessToken ? '•••••••• (unchanged if empty)' : 'Meta system user token'}
              required={sharedEnabled && !hasAccessToken}
            />
          </div>

          <div className="modal__footer" style={{ padding: 0, border: 0, marginTop: 16 }}>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PlatformSettings;
